/**
 * playerColors — ONE stable color per seat, shared by every surface that shows a player
 * (chat message names, roster avatars, dice roll toasts, voting cards). The point is
 * trackability: "the purple one is rolling" should hold from the roster to the toast to
 * the ballot without the viewer re-learning who's who.
 *
 * Colors are assigned by sorting the seat ids lexically and indexing into the palette,
 * so every component that builds the map from the same roster gets the same answer —
 * no ordering coupling between components, no props threading required (though passing
 * one memoized map down is cheaper than rebuilding per render).
 */

// Same palette VotingModal has used since v1 — kept verbatim so vote-card colors
// don't shift for players who've already internalized them.
export const PLAYER_COLOR_PALETTE = [
  '#6366f1', '#ef4444', '#22c55e', '#f59e0b', '#ec4899',
  '#14b8a6', '#f97316', '#8b5cf6', '#3b82f6', '#e11d48',
];

/** Stable id for a roster entry regardless of which shape it arrives in. */
export function playerColorKey(playerLike) {
  if (!playerLike) return null;
  const id = playerLike.game_player_id || playerLike.id;
  return id != null ? String(id) : null;
}

/**
 * Build { seatId → color } from a roster. Ids are deduped and sorted so the mapping is
 * stable across polls, re-renders, and independently-built maps.
 */
export function buildPlayerColorMap(playersOrIds = []) {
  const ids = [...new Set(
    playersOrIds
      .map(p => (typeof p === 'object' ? playerColorKey(p) : (p != null ? String(p) : null)))
      .filter(Boolean)
  )].sort();
  const map = {};
  ids.forEach((id, i) => { map[id] = PLAYER_COLOR_PALETTE[i % PLAYER_COLOR_PALETTE.length]; });
  return map;
}

/** One-shot lookup when you don't have a prebuilt map handy. */
export function playerColorFor(players, playerId) {
  if (playerId == null) return null;
  return buildPlayerColorMap(players)[String(playerId)] || null;
}
